import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useTicketBooking } from '@/features/booking/hooks/useTicketBooking';
import { useLoadingContext, useAuth } from '@/hooks';


import { MobileHeroSection } from './MobileHeroSection';
import { MobileTierRibbon } from './MobileTierRibbon';
import { MobileBookingCategoryHero } from './MobileBookingCategoryHero';
import { MobileAllowedAreaSection } from './MobileAllowedAreaSection';
import MobileAddOnSelector from './MobileAddOnSelector';
import MobileTicketFloatingSummary from './MobileTicketFloatingSummary';
import MobileAddOnDetailPopup from './MobileAddOnDetailPopup';
import { PerkCarousel } from '@/features/booking/components/PerkCarousel';
import type { AddOnScheduleData } from '@/features/booking/types/ticketBookingTypes';
import { TableBookingSkeleton } from '@/components/skeleton';

const TicketBookingMobile: React.FC = () => {
  const { eventBookingCategoryId } = useParams<{
    eventBookingCategoryId: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = useLoadingContext();
  const { isGuest, isAuthenticated } = useAuth();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const bookingSectionRef = React.useRef<HTMLDivElement>(null);
  
  const [hasScrolledToIntro, setHasScrolledToIntro] = useState(false);


  // Extract cartType and cartItemId from query parameters
  const cartTypeParam = searchParams.get('cartType');
  const initialCartTab: 'main' | 'guest' | null =
    cartTypeParam === 'guest' || cartTypeParam === 'main' ? cartTypeParam : null;
  const cartItemIdParam = searchParams.get('cartItemId');

  const {
    eventSchedule,
    eventBookingCategory,
    guestCount,
    addOnIdsPerGuest,
    selectedAddOns,
    loading,
    error,
    addingToCart,
    priceBreakdown,
    setGuestCount,
    handleAddOnToggle,
    setAddOnQuantityForGuest,
    addToCart,
    updateCart,
    deleteCartItem,
    incompleteCarts,
    selectedCartTab,
    currentCart,
    isLoadingCarts,
    isUpdatingCart,
    hasCartItemForEBC,
    hasCartChanges,
    switchCartTab,
    getItemCountForTab,
  } = useTicketBooking(eventBookingCategoryId || '', initialCartTab, cartItemIdParam);

  const pageCurrency = eventBookingCategory?.currency;
  const timezone = eventSchedule?.timezone_id;

  // Modal state for Addons
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAddOn, setSelectedAddOn] = useState<AddOnScheduleData | null>(null);

  // Redirect to table booking page if this is a table booking
  useEffect(() => {
    if (eventBookingCategory && eventBookingCategory.booking_category.is_table_booking) {
      const cartTypeParam = searchParams.get('cartType');
      const queryString = cartTypeParam ? `?cartType=${cartTypeParam}` : '';
      navigate(`/booking/reservations/${eventBookingCategoryId}${queryString}`, { replace: true });
    }
  }, [eventBookingCategory, eventBookingCategoryId, navigate, searchParams]);

  // Trigger loading state
  useEffect(() => {
    if (loading) {
      startLoading('ticket-booking-data');
    } else {
      stopLoading('ticket-booking-data');
    }

    return () => {
      stopLoading('ticket-booking-data');
    };
  }, [loading, startLoading, stopLoading]);

  // Detect when the booking section scrolls into view (both manual and programmatic)
  useEffect(() => {
    const container = scrollRef.current;
    const bookingEl = bookingSectionRef.current;
    if (!container || !bookingEl || hasScrolledToIntro) return;

    const checkVisibility = () => {
      const containerRect = container.getBoundingClientRect();
      const bookingRect = bookingEl.getBoundingClientRect();
      if (bookingRect.top < containerRect.bottom - 100) {
        setHasScrolledToIntro(true);
      }
    };

    // Check immediately in case the section is already visible
    checkVisibility();

    container.addEventListener('scroll', checkVisibility, { passive: true });
    return () => container.removeEventListener('scroll', checkVisibility);
  }, [hasScrolledToIntro, loading]);


  const handleAddToCart = async () => {
    // Check if we should update cart or add to cart
    // Close modal if it's open upon update or add
    setIsModalOpen(false);

    // Handle delete case: when guest count is 0 and there's a cart item
    if (currentCart && hasCartItemForEBC && guestCount === 0) {
      // Delete the cart item using delete API
      await deleteCartItem();
      // Cart count will auto-refresh via header's stale-while-revalidate
      // Reset guest count visually back to 1
      setGuestCount(1);
      return;
    }

    if (currentCart && hasCartItemForEBC && hasCartChanges) {
      // Update existing cart item
      await updateCart();
      // Cart count will auto-refresh via header's stale-while-revalidate
    } else if (currentCart && hasCartItemForEBC && !hasCartChanges) {
      // No changes - navigate to cart page
      navigate(`/carts/${currentCart.id}`);
    } else {
      // Add new item to cart (either no cart, or cart exists but no item for this EBC)
      await addToCart();
      // Cart count will auto-refresh via header's stale-while-revalidate
    }
  };

  const handleBack = () => {
    if (eventSchedule?.event?.business?.handle && eventSchedule?.event?.id) {
      navigate(`/venues/${eventSchedule.event.business.handle}/events/${eventSchedule.event.id}`);
    } else {
      navigate(-1);
    }
  };

  const handleAddOnClick = (addOn: AddOnScheduleData) => {
    setSelectedAddOn(addOn);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedAddOn(null);
  };

  const handleModalToggle = (addOnId: string, isSelected: boolean, guestIndex?: number) => {
    handleAddOnToggle(addOnId, isSelected, guestIndex);
  };

  const handleQuantityChange = (addOnId: string, guestIndex: number, quantity: number) => {
    setAddOnQuantityForGuest(addOnId, guestIndex, quantity);
  };

  if (loading) {
     return <TableBookingSkeleton />;
  }

  if (error || !eventSchedule || !eventBookingCategory) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Booking Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error || 'The booking option you are looking for does not exist.'}
          </p>
          <button 
            onClick={handleBack}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Overwrite perks to empty array as per tickets logic "tickets dont have perks"
  const modifiedEventBookingCategory = {
    ...eventBookingCategory,
    booking_category: {
      ...eventBookingCategory.booking_category,
      perks: []
    }
  };

  return (
    <div ref={scrollRef} className="bg-[#050505] fixed inset-0 text-white font-sans w-full max-w-[100vw] z-0 flex flex-col overflow-y-auto overflow-x-hidden overscroll-none">
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: { 
            background: '#1f2937', 
            color: '#fff',
            border: '1px solid #374151'
          },
          duration: 3000,
        }} 
      />
      
      {/* 1. Mobile Hero Section */}
      <MobileHeroSection 
        event={eventSchedule?.event} 
        business={eventBookingCategory?.booking_category?.business || eventSchedule?.event?.business} 
        isAuthenticated={isAuthenticated}
        scrollRef={scrollRef}
        onBack={() => {
          if (eventSchedule?.event?.business?.handle && eventSchedule?.event?.id && eventSchedule?.id) {
            navigate(`/venues/${eventSchedule.event.business.handle}/events/${eventSchedule.event.id}/booking/${eventSchedule.id}`);
          } else {
            navigate(-1);
          }
        }}
      />
      
      {/* Scrollable Main Content Below */}
      <main className="flex-1 pb-[200px] relative z-10 w-full flex flex-col">
        {/* Tier Ribbon - Sticky */}
        <div className="sticky top-16 z-40 bg-[#050505]">
          <MobileTierRibbon 
            tierClass={eventBookingCategory?.booking_category?.class}
            startTime={eventSchedule?.start_time}
            timezoneId={eventSchedule?.timezone_id}
          />
        </div>

        {/* 3. Booking Category Hero (Name, Desc, Price, Perks) */}
        {eventBookingCategory && (
          <MobileBookingCategoryHero 
            eventBookingCategory={modifiedEventBookingCategory as any} 
          />
        )}

        {/* 4. Allowed Areas Carousel */}
        {eventBookingCategory?.booking_category?.allowed_areas && (
          <MobileAllowedAreaSection allowedAreas={eventBookingCategory.booking_category.allowed_areas} />
        )}

        {/* 5. Perks Carousel */}
        {eventBookingCategory?.booking_category?.perks && (
          <PerkCarousel perks={eventBookingCategory.booking_category.perks} />
        )}


        {/* Cart Tabs */}
        {!isLoadingCarts && incompleteCarts && incompleteCarts.length > 0 && (() => {
          const mainCart = incompleteCarts.find(c => !c.is_guest_cart);
          const guestCart = incompleteCarts.find(c => c.is_guest_cart);
          const mainCartHasItems = mainCart?.items && mainCart.items.length > 0;
          const guestCartHasItems = guestCart?.items && guestCart.items.length > 0;
          
          if (!mainCartHasItems && !guestCartHasItems) {
            return null;
          }
          
          const showTabs = !!guestCart && guestCartHasItems && (mainCartHasItems || !isGuest);
          
          if (!showTabs) {
            return null;
          }
          
          const showMainCartTab = mainCartHasItems || (guestCart && !isGuest);
          
          return (
            <div className="w-full px-4 pt-4 pb-2 border-b border-gray-800">
              <div className="flex gap-4">
                {showMainCartTab && (
                  <button
                    onClick={() => switchCartTab('main')}
                    className={`pb-2 font-medium transition-colors relative ${
                      selectedCartTab === 'main'
                        ? 'text-white border-b-2 border-blue-500'
                        : 'text-gray-400 hover:text-gray-300 border-transparent'
                    }`}
                  >
                    Main Cart{getItemCountForTab('main') > 0 ? ` (${getItemCountForTab('main')})` : ''}
                  </button>
                )}
                
                <button
                  onClick={() => switchCartTab('guest')}
                  className={`pb-2 font-medium transition-colors relative ${
                    selectedCartTab === 'guest'
                      ? 'text-white border-b-2 border-blue-500'
                      : 'text-gray-400 hover:text-gray-300 border-transparent'
                  }`}
                >
                  Guest Cart{getItemCountForTab('guest') > 0 ? ` (${getItemCountForTab('guest')})` : ''}
                </button>
              </div>
            </div>
          );
        })()}

        <div className="mt-8 scroll-mt-32" ref={bookingSectionRef}>
           {/* Add-ons List Grid */}
           <MobileAddOnSelector
              addOns={eventBookingCategory?.add_on_schedules || []}
              selectedAddOns={selectedAddOns}
              onAddOnToggle={handleAddOnToggle}
              onAddOnClick={handleAddOnClick}
              guestCount={guestCount}
              addOnIdsPerGuest={addOnIdsPerGuest}
              currency={pageCurrency}
            />
        </div>
      </main>

      {/* Floating Ticket Price Summary */}
      <MobileTicketFloatingSummary
        priceBreakdown={priceBreakdown}
        guestCount={guestCount}
        onGuestCountChange={setGuestCount}
        eventBookingCategory={eventBookingCategory as any}
        onAddToCart={handleAddToCart}
        addingToCart={addingToCart || isUpdatingCart}
        buttonText={currentCart && hasCartItemForEBC ? 'Update Cart' : 'Add to Cart'}
        isUpdateMode={!!(currentCart && hasCartItemForEBC)}
        onUpdateCart={currentCart && hasCartItemForEBC ? handleAddToCart : undefined}
        cartId={currentCart?.id || null}
        addOnCount={Object.values(selectedAddOns).reduce((sum, count) => sum + count, 0)}
        isLoadingCarts={isLoadingCarts}
        hasCartChanges={hasCartChanges}
        hasViewCartButton={!!(currentCart && currentCart.items && currentCart.items.length > 0)}
        hasCartItemForEBC={hasCartItemForEBC}
        currency={pageCurrency}
        onViewCart={() => currentCart && navigate(`/carts/${currentCart.id}`)}
      />

       {/* Add-on Detail Popup */}
       <MobileAddOnDetailPopup
        isOpen={isModalOpen}
        onClose={handleModalClose}
        addOn={selectedAddOn}
        isSelected={selectedAddOn ? selectedAddOns[selectedAddOn.add_on.id] > 0 : false}
        onToggle={handleModalToggle}
        onQuantityChange={handleQuantityChange}
        guestCount={guestCount}
        remainingInventory={selectedAddOn?.remaining_inventory ?? null}
        addOnIdsPerGuest={addOnIdsPerGuest}
        allAddOns={eventBookingCategory?.add_on_schedules || []}
        currency={pageCurrency || 'USD'}
        eventStartTime={eventSchedule?.start_time}
        earlyEntryDuration={eventSchedule?.early_entry_duration || null}
        timezone={timezone}
      />
    </div>
  );
};

export default TicketBookingMobile;
